import { Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface TypedDeleteConfirmationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	entityType: string;
	entityName: string;
	consequences: string;
	onConfirm: () => void | Promise<void>;
	isDeleting?: boolean;
}

export function TypedDeleteConfirmationDialog({
	open,
	onOpenChange,
	entityType,
	entityName,
	consequences,
	onConfirm,
	isDeleting = false,
}: TypedDeleteConfirmationDialogProps) {
	const inputId = useId();
	const [confirmationText, setConfirmationText] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const deletionInProgress = isDeleting || isSubmitting;
	const isConfirmed = entityName.length > 0 && confirmationText === entityName;

	useEffect(() => {
		if (!open) {
			setConfirmationText("");
			setIsSubmitting(false);
		}
	}, [open]);

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen && deletionInProgress) return;
		onOpenChange(nextOpen);
	};

	const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (!isConfirmed || deletionInProgress) return;

		setIsSubmitting(true);
		try {
			await onConfirm();
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Delete {entityType}?</AlertDialogTitle>
					<AlertDialogDescription>
						This permanently deletes {entityType.toLowerCase()} &quot;
						{entityName}&quot;. {consequences}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="space-y-2">
					<Label htmlFor={inputId}>
						Type <span className="font-mono font-semibold">{entityName}</span>{" "}
						to confirm
					</Label>
					<Input
						id={inputId}
						value={confirmationText}
						onChange={(event) => setConfirmationText(event.target.value)}
						placeholder={entityName}
						autoComplete="off"
						autoFocus
						disabled={deletionInProgress}
					/>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={deletionInProgress}>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={handleConfirm}
						disabled={!isConfirmed || deletionInProgress}
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
					>
						{deletionInProgress ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : null}
						Delete
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
